import * as React from "react";
import { styled } from "@mui/material/styles";
import {
  Button,
  Typography,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  Box,
  Grid,
  PopperPlacementType,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CloseIcon from "@mui/icons-material/Close";
import clsx from "clsx";
import MoreInfoButton from "./MoreInfoButton";

const PREFIX = "MenuPopup";

const classes = {
  popperStyles: `${PREFIX}-popperStyles`,
  largePopup: `${PREFIX}-largePopup`,
  mediumPopup: `${PREFIX}-mediumPopup`,
  smallPopup: `${PREFIX}-smallPopup`,
  popup: `${PREFIX}-popup`,
  paper: `${PREFIX}-paper`,
  closeIcon: `${PREFIX}-closeIcon`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.popperStyles}`]: {
    "zIndex": 100,
    "marginLeft": -2,
    "& .MuiFormGroup-root": {
      [theme.breakpoints.down("md")]: {
        width: 300,
      },
    },
  },

  [`& .${classes.largePopup}`]: {
    "& .MuiFormGroup-root": {
      width: 1000,
      maxHeight: 575,
      [theme.breakpoints.down("md")]: {
        width: "100%",
        overflow: "scroll",
        display: "grid",
      },
    },
  },

  [`& .${classes.mediumPopup}`]: {
    "& .MuiFormGroup-root": {
      width: 500,
      maxHeight: 500,
    },
  },

  [`& .${classes.smallPopup}`]: {
    "& .MuiBox-root": { paddingLeft: 20 },
    "& .MuiFormGroup-root": {
      maxWidth: 1000,
      maxHeight: 500,
      display: "grid",
      gridTemplateRows: "auto",
      gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr",
      overflow: "auto",
    },
  },

  [`& .${classes.popup}`]: {
    "position": "relative",
    "paddingTop": 20,
    [theme.breakpoints.down("md")]: {
      borderRadius: 4,
      border: `1px solid ${theme.palette.primary.light}`,
    },
    "& button": {
      minWidth: 40,
      position: "absolute",
      zIndex: 10,
      top: 0,
      right: 0,
    },
    "& .MuiFormGroup-root": {
      padding: 10,
    },
  },

  [`& .${classes.paper}`]: {
    backgroundColor: "#fbfcfd",
    boxShadow: `10px 8px 8px -8px ${theme.palette.primary.light}`,
  },

  [`& .${classes.closeIcon}`]: {
    "color": "#acc8df",
    "&:focus": {
      background: "rgba(0, 0, 0, 0.04)",
    },
  },
}));

interface Props {
  id: string;
  label: string | React.ReactNode;
  popperPosition: PopperPlacementType;
  children: React.ReactNode;
  disabled?: boolean;
  info?: string;
  width?: "large" | "medium" | "small";
  pagination?: React.ReactNode;
}

export default function MenuPopup({
  id,
  label,
  popperPosition,
  children,
  disabled,
  width,
  info,
  pagination,
}: Props) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const open = Boolean(anchorEl);

  const widthClasses = {
    large: classes.largePopup,
    medium: classes.mediumPopup,
    small: classes.smallPopup,
  };
  return (
    <Root>
      {info ? (
        <Typography
          component="div"
          className="disabledInfo"
          sx={{
            "& button": {
              position: "absolute",
              right: 10,
              top: 10,
            },
          }}
        >
          <MoreInfoButton
            hover
            id="moreInfo"
            label="more Info"
            popperPosition="bottom-start"
          >
            {info}
          </MoreInfoButton>
        </Typography>
      ) : undefined}
      <Button
        id={`${id}_button`}
        disabled={disabled}
        onClick={handleClick}
        aria-controls={open ? id : undefined}
        aria-haspopup="true"
        sx={(theme) =>
          open
            ? {
                "width": "100%",
                "padding": "8px",
                "height": "auto",
                "alignItems": "center",
                "&.Mui-focusVisible, &:hover": {
                  backgroundColor: "#cdd7e3",
                },
                "& .MuiGrid-root": {
                  display: "inline-flex",
                },
                "& p": {
                  textAlign: "left",
                  color: theme.palette.primary.main,
                  fontSize: "1rem",
                },
                "& svg": {
                  position: "absolute",
                  right: 0,
                  color: theme.palette.primary.main,
                },
                "&.Mui-disabled p,&.Mui-disabled  svg": {
                  color: "rgba(0, 0, 0, 0.26)",
                },
                "&.MuiButtonBase-root": {
                  "transition": ".6s ease",
                  "background": "#fbfcfd",
                  "boxShadow": `10px 6px 9px -4px ${theme.palette.primary.main}`,
                  "borderTopRightRadius": 0,
                  "borderBottomRightRadius": 0,

                  [theme.breakpoints.down("md")]: {
                    borderTopRightRadius: 4,
                    borderBottomRightRadius: 4,
                  },
                  "&:hover": {
                    backgroundColor: "#fbfcfd",
                  },
                },
              }
            : {
                "width": "100%",
                "padding": "8px",
                "height": "auto",
                "alignItems": "center",
                "&.Mui-focusVisible, &:hover": {
                  backgroundColor: "#cdd7e3",
                },
                "& .MuiGrid-root": {
                  display: "inline-flex",
                },
                "& p": {
                  textAlign: "left",
                  color: theme.palette.primary.main,
                  fontSize: "1rem",
                },
                "& svg": {
                  position: "absolute",
                  right: 0,
                  color: theme.palette.primary.main,
                },
                "&.Mui-disabled p,&.Mui-disabled  svg": {
                  color: "rgba(0, 0, 0, 0.26)",
                },
              }
        }
      >
        <Grid container sx={{ width: "100%" }}>
          <Grid size={10}>
            <Typography sx={{ width: "inherit" }}>{label}</Typography>
          </Grid>
          <Grid size={2}>
            {info ? undefined : popperPosition === "right" ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </Grid>
        </Grid>
      </Button>
      <Popper
        open={open}
        anchorEl={anchorEl}
        id="menu-popup-content"
        className={clsx(classes.popperStyles, width ? widthClasses[width] : "")}
        placement={popperPosition}
        disablePortal
      >
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Grow in={open}>
            <Paper className={classes.paper}>
              <Box id={id} className={classes.popup}>
                <Button title="Close" onClick={() => setAnchorEl(null)}>
                  <CloseIcon className={classes.closeIcon} />
                </Button>
                {children}
              </Box>
              {pagination}
            </Paper>
          </Grow>
        </ClickAwayListener>
      </Popper>
    </Root>
  );
}
